/**
 * IconsPage — manage custom SVG icons per action for the current sport.
 *
 * Coaches see every action their sport supports, preview the current icon
 * (custom SVG > bundled SVG > emoji), and can paste a new SVG to override.
 * Overrides live in IndexedDB (see src/utils/customIcons.js) and are read
 * by ActionIcon at render time.
 *
 * Security: every paste hits DOMPurify's SVG profile before storage, and
 * again before render. See sanitizeSvg().
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import basketballConfig from '../../sports/basketball';
import { ActionIcon } from '../studio/ActionIcons';
import {
  getAllCustomIcons, setCustomIcon, deleteCustomIcon, sanitizeSvg, bumpCacheVersion,
} from '../../utils/customIcons';

const SPORT_ID = 'basketball';

const styles = {
  page: {
    padding: '20px 16px 80px', color: '#e2e8f0', minHeight: '100vh',
    background: '#0f172a',
    fontFamily: "'IBM Plex Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  },
  header: { marginBottom: 16 },
  title: { fontSize: 20, fontWeight: 700, letterSpacing: 0.5, margin: 0 },
  subtitle: { color: '#94a3b8', fontSize: 12, marginTop: 4 },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
    gap: 12,
  },
  card: {
    background: 'linear-gradient(180deg, rgba(30,41,59,0.7), rgba(15,23,42,0.9))',
    border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: 10, padding: 12,
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    minHeight: 120,
  },
  iconWell: {
    width: 46, height: 46, borderRadius: '50%',
    background: 'rgba(15,23,42,0.9)',
    border: '1px solid rgba(255,255,255,0.12)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#f97316', marginBottom: 6,
  },
  label: { fontSize: 12, fontWeight: 600, textAlign: 'center', marginBottom: 2 },
  id: { fontSize: 10, color: '#64748b', textAlign: 'center', marginBottom: 8 },
  actionRow: { display: 'flex', gap: 6, marginTop: 'auto' },
  btn: {
    flex: 1, padding: '5px 8px', borderRadius: 6,
    background: 'rgba(59,130,246,0.15)', color: '#93c5fd',
    border: '1px solid rgba(59,130,246,0.3)',
    fontSize: 10, fontWeight: 600, cursor: 'pointer',
  },
  resetBtn: {
    flex: 1, padding: '5px 8px', borderRadius: 6,
    background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
    border: '1px solid rgba(239,68,68,0.3)',
    fontSize: 10, fontWeight: 600, cursor: 'pointer',
  },
  modal: {
    position: 'fixed', inset: 0, zIndex: 200,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    background: 'rgba(0,0,0,0.7)', padding: 16,
  },
  modalBox: {
    width: '100%', maxWidth: 480,
    background: '#0f172a', borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.1)',
    padding: 16, color: '#e2e8f0',
    maxHeight: '90vh', overflowY: 'auto',
  },
  textarea: {
    width: '100%', minHeight: 160, resize: 'vertical',
    background: '#020617', color: '#e2e8f0',
    border: '1px solid rgba(255,255,255,0.15)', borderRadius: 8,
    padding: 10, fontSize: 11, fontFamily: 'ui-monospace, SFMono-Regular, monospace',
    marginTop: 8, boxSizing: 'border-box',
  },
  previewRow: {
    display: 'flex', gap: 12, alignItems: 'center',
    padding: 10, background: 'rgba(15,23,42,0.6)', borderRadius: 8,
    marginTop: 10,
  },
  previewBox: {
    width: 56, height: 56, borderRadius: '50%',
    background: 'rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.15)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#f97316',
  },
  errorText: { color: '#fca5a5', fontSize: 11, marginTop: 6 },
  modalActions: { display: 'flex', gap: 8, marginTop: 14 },
  primaryBtn: {
    flex: 1, padding: '8px 12px', borderRadius: 8,
    background: '#3b82f6', color: '#fff', border: 'none',
    fontWeight: 600, cursor: 'pointer',
  },
  ghostBtn: {
    flex: 1, padding: '8px 12px', borderRadius: 8,
    background: 'transparent', color: '#94a3b8',
    border: '1px solid rgba(255,255,255,0.15)',
    fontWeight: 600, cursor: 'pointer',
  },
};

const ALL_ACTIONS = [
  ...(basketballConfig.actions?.offense || []),
  ...(basketballConfig.actions?.defense || []),
  ...(basketballConfig.actions?.transition || []),
];

const IconsPage = () => {
  const [customMap, setCustomMap] = useState({});
  const [editing, setEditing] = useState(null);   // action being edited
  const [draftSvg, setDraftSvg] = useState('');
  const [error, setError] = useState('');

  const reload = useCallback(() => {
    getAllCustomIcons().then(setCustomMap);
  }, []);
  useEffect(() => { reload(); }, [reload]);

  const previewSvg = useMemo(() => (draftSvg ? sanitizeSvg(draftSvg) : ''), [draftSvg]);

  const openEditor = (action) => {
    setEditing(action);
    const key = `${SPORT_ID}:${action.id}`;
    setDraftSvg(customMap[key] || '');
    setError('');
  };

  const closeEditor = () => {
    setEditing(null);
    setDraftSvg('');
    setError('');
  };

  const save = async () => {
    if (!editing) return;
    try {
      await setCustomIcon(SPORT_ID, editing.id, draftSvg);
      bumpCacheVersion();
      await new Promise((r) => setTimeout(r, 100));
      reload();
      closeEditor();
    } catch (e) {
      setError(e.message || 'Save failed');
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.svg$/i.test(file.name) && file.type !== 'image/svg+xml') {
      setError('File must be .svg');
      return;
    }
    const text = await file.text();
    setDraftSvg(text);
    setError('');
  };

  const resetToDefault = async (action) => {
    await deleteCustomIcon(SPORT_ID, action.id);
    bumpCacheVersion();
    reload();
  };

  const isCustom = (actionId) => !!customMap[`${SPORT_ID}:${actionId}`];

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>Custom Icons</h1>
        <p style={styles.subtitle}>
          Basketball — {ALL_ACTIONS.length} actions. Custom SVGs are sanitized (DOMPurify) and stored locally in this browser.
        </p>
      </div>

      <div style={styles.grid}>
        {ALL_ACTIONS.map((action) => (
          <div key={action.id} style={styles.card}>
            <div style={styles.iconWell}>
              <ActionIcon actionId={action.id} sportId={SPORT_ID} size={26} emojiFallback={action.icon} />
            </div>
            <div style={styles.label}>{action.name || action.id}</div>
            <div style={styles.id}>
              {action.id}{isCustom(action.id) ? ' • custom' : ''}
            </div>
            <div style={styles.actionRow}>
              <button style={styles.btn} onClick={() => openEditor(action)}>Edit</button>
              {isCustom(action.id) && (
                <button style={styles.resetBtn} onClick={() => resetToDefault(action)}>Reset</button>
              )}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div style={styles.modal} onClick={closeEditor}>
          <div style={styles.modalBox} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>
              Edit icon: {editing.name || editing.id}
            </div>
            <p style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>
              Paste raw SVG or upload a .svg file. Use <code>currentColor</code> for stroke/fill
              so the wheel accent tints the icon.
            </p>

            <label style={{ display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 8 }}>
              Upload:
              <input type="file" accept=".svg,image/svg+xml" onChange={handleFile}
                style={{ display: 'block', marginTop: 4 }} />
            </label>

            <label style={{ display: 'block', fontSize: 11, color: '#94a3b8', marginTop: 10 }}>
              Or paste SVG source:
            </label>
            <textarea
              style={styles.textarea}
              value={draftSvg}
              onChange={(e) => setDraftSvg(e.target.value)}
              placeholder='<svg viewBox="0 0 24 24" stroke="currentColor" fill="none" stroke-width="1.6">...</svg>'
            />

            <div style={styles.previewRow}>
              <div style={styles.previewBox}>
                {previewSvg
                  ? <span style={{ display: 'inline-flex', width: 32, height: 32, color: 'currentColor', alignItems: 'center', justifyContent: 'center' }}
                      dangerouslySetInnerHTML={{ __html: previewSvg }} />
                  : <ActionIcon actionId={editing.id} sportId={SPORT_ID} size={32} emojiFallback={editing.icon} />}
              </div>
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                Sanitized preview
                {previewSvg && (
                  <div style={{ color: '#22c55e', fontSize: 10, marginTop: 2 }}>
                    ✓ Passes sanitizer
                  </div>
                )}
              </div>
            </div>

            {error && <div style={styles.errorText}>{error}</div>}

            <div style={styles.modalActions}>
              <button style={styles.ghostBtn} onClick={closeEditor}>Cancel</button>
              <button
                style={{ ...styles.primaryBtn, opacity: previewSvg ? 1 : 0.5, pointerEvents: previewSvg ? 'auto' : 'none' }}
                onClick={save}
              >
                Save
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IconsPage;
